"use client";

import { useState } from "react";
import {
  Box,
  Button,
  Pagination,
  TextareaAutosize,
  Typography,
  styled,
} from "@mui/material";
import toast from "react-hot-toast";
import useCandidateNotes from "@lib/candidates/useCandidateNotes";
import useCreateCandidateNote from "@lib/candidates/useCreateCandidateNote";
import useDeleteCandidateNote from "@lib/candidates/useDeleteCandidateNote";
import ConfirmDialog from "@components/shared/ConfirmDialog";
import NoteItem from "./NoteItem";

const NoteInput = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  fontFamily: "inherit",
  fontSize: 14,
  borderRadius: theme.shape.borderRadius,
  borderColor: theme.palette.divider,
  resize: "vertical",
}));

export default function NotesTab({ candidateId }) {
  const [page, setPage] = useState(1);
  const [content, setContent] = useState("");
  const [noteToDelete, setNoteToDelete] = useState(null);

  const { notes, lastPage, isLoading } = useCandidateNotes(candidateId, page);
  const create = useCreateCandidateNote(candidateId);
  const remove = useDeleteCandidateNote(candidateId);

  const handleConfirmDelete = () => {
    if (!noteToDelete) {
      return;
    }

    const noteId = noteToDelete.id;
    setNoteToDelete(null);

    remove.mutate(noteId, {
      onSuccess: () => {
        toast.success("Note deleted");
      },
      onError: () => {
        toast.error("Failed to delete note");
      },
    });
  };

  const handleAdd = () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Please write a note");
      return;
    }

    create.mutate(trimmed, {
      onSuccess: () => {
        toast.success("Note added");
        setContent("");
        setPage(1);
      },
      onError: () => {
        toast.error("Failed to add note");
      },
    });
  };

  return (
    <Box>
      <NoteInput
        minRows={4}
        placeholder="Write a note..."
        value={content}
        onChange={(event) => setContent(event.target.value)}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={create.isPending}
        >
          Add note
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        {!isLoading && notes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No notes yet.
          </Typography>
        )}
        {notes.map((note) => (
          <NoteItem key={note.id} note={note} onDelete={setNoteToDelete} />
        ))}
      </Box>

      {lastPage > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={lastPage}
            page={page}
            onChange={(_event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(noteToDelete)}
        title="Delete note"
        message="Are you sure you want to delete this note?"
        onConfirm={handleConfirmDelete}
        onClose={() => setNoteToDelete(null)}
      />
    </Box>
  );
}
